/**
 * Panneau de choix des traceurs, partagé par la bannière de consentement et la page `/cookies`.
 *
 * Un seul formulaire pour deux surfaces : les catégories, les libellés et le comportement de rejet
 * ne peuvent pas diverger entre le bandeau affiché au premier passage et la page de modification.
 *
 * Accessibilité (DECISIONS §9.5) : case à cocher native (lisible au lecteur d’écran, activable au
 * clavier), cible tactile de 44 px portée par la classe `.case-a-cocher`, et catégorie essentielle
 * annoncée « toujours active » plutôt que masquée — le joueur doit voir ce qui reste actif.
 */
import { useTranslation } from 'react-i18next';
import { useId } from 'react';

import { Bouton } from './Bouton';

export interface ProprietesPanneauTraceurs {
  /** État de la mesure d’audience facultative. */
  readonly mesure: boolean;
  readonly onChangerMesure: (valeur: boolean) => void;
  readonly onEnregistrer: () => void;
  /** Absent sur la page `/cookies` : le panneau y est permanent, sans retour. */
  readonly onAnnuler?: () => void;
}

export function PanneauTraceurs({
  mesure,
  onChangerMesure,
  onEnregistrer,
  onAnnuler,
}: ProprietesPanneauTraceurs) {
  const { t } = useTranslation();
  const idMesure = useId();

  return (
    <div className="traceurs" data-panneau-traceurs="oui">
      <h3 className="traceurs__titre">{t('cookies.personnaliser.titre')}</h3>
      <div className="traceurs__categorie">
        <div className="traceurs__entete">
          <p className="traceurs__nom">{t('cookies.personnaliser.essentielsTitre')}</p>
          <p className="traceurs__etat">{t('cookies.personnaliser.toujoursActif')}</p>
        </div>
        <p className="traceurs__texte">{t('cookies.personnaliser.essentielsTexte')}</p>
      </div>
      <div className="traceurs__categorie">
        <div className="traceurs__entete">
          <p className="traceurs__nom">{t('cookies.personnaliser.mesureTitre')}</p>
          <p className="traceurs__etat">
            {mesure
              ? t('cookies.personnaliser.mesureActive')
              : t('cookies.personnaliser.mesureInactive')}
          </p>
        </div>
        <p className="traceurs__texte" id={`${idMesure}-texte`}>
          {t('cookies.personnaliser.mesureTexte')}
        </p>
        {/* Une seule étiquette pour la case : le nom accessible du contrôle est exactement le
            libellé de la catégorie, et l’explication est reliée par `aria-describedby`. */}
        <label className="case-a-cocher" htmlFor={idMesure}>
          <input
            id={idMesure}
            type="checkbox"
            checked={mesure}
            aria-describedby={`${idMesure}-texte`}
            onChange={(evenement) => {
              onChangerMesure(evenement.target.checked);
            }}
          />
          <span>{t('cookies.personnaliser.mesureTitre')}</span>
        </label>
      </div>
      <div className="traceurs__actions">
        <Bouton variante="principal" onClick={onEnregistrer}>
          {t('cookies.bandeau.enregistrer')}
        </Bouton>
        {onAnnuler === undefined ? null : (
          <Bouton variante="discret" onClick={onAnnuler}>
            {t('cookies.bandeau.retour')}
          </Bouton>
        )}
      </div>
    </div>
  );
}
